import {
  useCallback,
  type Dispatch,
  type MouseEvent as ReactMouseEvent,
  type MutableRefObject,
  type SetStateAction,
} from "react";

import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import type { Point } from "@/lib/geometry";

import {
  openMeasurementContextMenuInteraction,
  startDraftMoveInteraction,
  startMeasurementLabelMoveInteraction,
  startMeasurementLabelResizeInteraction,
  startMeasurementMoveInteraction,
  startMeasurementResizeInteraction,
  startMeasurementVertexDragInteraction,
} from "./transform-interactions";
import type {
  InteractionState,
  LabelResizeHandle,
  MeasurementOverlayContextMenuState,
  ResizeHandle,
} from "./types";
import type {
  DraftMeasurementState,
  MeasurementAnnotation,
  MeasurementGeometry,
  MeasurementOverlayRecord,
  PolygonGeometry,
} from "../measurement-overlay-types";

interface UseMeasurementOverlayTransformActionsArgs {
  getPagePoint: (clientX: number, clientY: number) => Point | null;
  overlayRef: MutableRefObject<HTMLDivElement | null>;
  lastPointerPagePointRef: MutableRefObject<Point | null>;
  interactionRef: MutableRefObject<InteractionState | null>;
  drawingDraftRef: MutableRefObject<DraftMeasurementState | null>;
  measurements: MeasurementOverlayRecord[];
  selectedMeasurementIds: Id<"takeoffPilotMeasurements">[];
  handleCancelDraft: () => void;
  onSelectMeasurement: (
    measurementId: Id<"takeoffPilotMeasurements"> | null,
    options?: { shiftKey?: boolean }
  ) => void;
  setInteraction: Dispatch<SetStateAction<InteractionState | null>>;
  setContextMenu: Dispatch<SetStateAction<MeasurementOverlayContextMenuState | null>>;
  setPreviewGeometries: Dispatch<
    SetStateAction<Record<string, MeasurementGeometry | PolygonGeometry>>
  >;
}

export function useMeasurementOverlayTransformActions({
  getPagePoint,
  overlayRef,
  lastPointerPagePointRef,
  interactionRef,
  drawingDraftRef,
  measurements,
  selectedMeasurementIds,
  handleCancelDraft,
  onSelectMeasurement,
  setInteraction,
  setContextMenu,
  setPreviewGeometries,
}: UseMeasurementOverlayTransformActionsArgs) {
  const startDraftMove = useCallback(
    (event: ReactMouseEvent, draft: DraftMeasurementState) => {
      startDraftMoveInteraction({
        event,
        draft,
        drawingDraftRef,
        getPagePoint,
        interactionRef,
        setInteraction,
        setContextMenu,
      });
    },
    [drawingDraftRef, getPagePoint, interactionRef, setInteraction, setContextMenu]
  );

  const openContextMenu = useCallback(
    (event: ReactMouseEvent, measurement: MeasurementOverlayRecord) => {
      openMeasurementContextMenuInteraction({
        event,
        measurement,
        getPagePoint,
        overlayRef,
        lastPointerPagePointRef,
        handleCancelDraft,
        onSelectMeasurement: (measurementId) => onSelectMeasurement(measurementId),
        setContextMenu,
      });
    },
    [
      getPagePoint,
      handleCancelDraft,
      lastPointerPagePointRef,
      onSelectMeasurement,
      overlayRef,
      setContextMenu,
    ]
  );

  const startMeasurementMove = useCallback(
    (
      event: ReactMouseEvent,
      measurementId: Id<"takeoffPilotMeasurements">,
      geometry: MeasurementGeometry | PolygonGeometry
    ) => {
      if (interactionRef.current?.mode === "drawing") {
        return;
      }
      startMeasurementMoveInteraction({
        event,
        measurementId,
        geometry,
        getPagePoint,
        handleCancelDraft,
        measurements,
        selectedMeasurementIds,
        onSelectMeasurement: (selectedId, options) => onSelectMeasurement(selectedId, options),
        setPreviewGeometries,
        interactionRef,
        setInteraction,
      });
    },
    [
      getPagePoint,
      handleCancelDraft,
      interactionRef,
      measurements,
      onSelectMeasurement,
      selectedMeasurementIds,
      setInteraction,
      setPreviewGeometries,
    ]
  );

  const startMeasurementResize = useCallback(
    (
      event: ReactMouseEvent,
      measurementId: Id<"takeoffPilotMeasurements">,
      handle: ResizeHandle,
      geometry: MeasurementGeometry
    ) => {
      if (interactionRef.current?.mode === "drawing") {
        return;
      }
      startMeasurementResizeInteraction({
        event,
        measurementId,
        handle,
        geometry,
        getPagePoint,
        setPreviewGeometries,
        interactionRef,
        setInteraction,
      });
    },
    [getPagePoint, interactionRef, setInteraction, setPreviewGeometries]
  );

  const startMeasurementVertexDrag = useCallback(
    (
      event: ReactMouseEvent,
      measurementId: Id<"takeoffPilotMeasurements">,
      vertexIndex: number,
      geometry: PolygonGeometry
    ) => {
      if (interactionRef.current?.mode === "drawing") {
        return;
      }
      startMeasurementVertexDragInteraction({
        event,
        measurementId,
        vertexIndex,
        geometry,
        setPreviewGeometries,
        interactionRef,
        setInteraction,
      });
    },
    [interactionRef, setInteraction, setPreviewGeometries]
  );

  const openVertexContextMenu = useCallback(
    (
      event: ReactMouseEvent,
      measurementId: Id<"takeoffPilotMeasurements">,
      vertexIndex: number
    ) => {
      event.preventDefault();
      event.stopPropagation();
      const pagePoint = getPagePoint(event.clientX, event.clientY);
      const overlayRect = overlayRef.current?.getBoundingClientRect();
      if (pagePoint) {
        lastPointerPagePointRef.current = pagePoint;
      }
      handleCancelDraft();
      onSelectMeasurement(measurementId);
      setContextMenu({
        target: {
          type: "measurement-vertex",
          measurementId,
          vertexIndex,
        },
        x: overlayRect ? event.clientX - overlayRect.left : event.clientX,
        y: overlayRect ? event.clientY - overlayRect.top : event.clientY,
        pagePoint,
      });
    },
    [
      getPagePoint,
      handleCancelDraft,
      lastPointerPagePointRef,
      onSelectMeasurement,
      overlayRef,
      setContextMenu,
    ]
  );

  const startMeasurementLabelMove = useCallback(
    (
      event: ReactMouseEvent,
      measurementId: Id<"takeoffPilotMeasurements">,
      annotation: MeasurementAnnotation | undefined
    ) => {
      if (interactionRef.current?.mode === "drawing") {
        return;
      }
      startMeasurementLabelMoveInteraction({
        event,
        measurementId,
        annotation,
        getPagePoint,
        handleCancelDraft,
        onSelectMeasurement: (selectedId, options) => onSelectMeasurement(selectedId, options),
        interactionRef,
        setInteraction,
      });
    },
    [
      getPagePoint,
      handleCancelDraft,
      interactionRef,
      onSelectMeasurement,
      setInteraction,
    ]
  );

  const startMeasurementLabelResize = useCallback(
    (
      event: ReactMouseEvent,
      measurementId: Id<"takeoffPilotMeasurements">,
      handle: LabelResizeHandle,
      annotation: MeasurementAnnotation | undefined,
      initialWidth: number
    ) => {
      startMeasurementLabelResizeInteraction({
        event,
        measurementId,
        handle,
        annotation,
        initialWidth,
        getPagePoint,
        handleCancelDraft,
        onSelectMeasurement: (selectedId, options) => onSelectMeasurement(selectedId, options),
        interactionRef,
        setInteraction,
      });
    },
    [
      getPagePoint,
      handleCancelDraft,
      interactionRef,
      onSelectMeasurement,
      setInteraction,
    ]
  );

  return {
    startDraftMove,
    openContextMenu,
    startMeasurementMove,
    startMeasurementResize,
    startMeasurementVertexDrag,
    openVertexContextMenu,
    startMeasurementLabelMove,
    startMeasurementLabelResize,
  };
}

export type { UseMeasurementOverlayTransformActionsArgs };
